import type { Operation } from "../Machine";
import { MaterialInstance } from "../Materials";
import { makeToolItem } from "../material-helpers";
import type { ToolId } from "../Tool";
import { PLAN_IDS, PlanId } from "../plans";
import {
  assembleFromBlueprint,
  blueprintFor,
  blueprintInputs,
} from "./blueprint";
import { PLAN_RECORDS, PlanRecord } from "./plan-record";

/**
 * The operation a plan record comes to — what a bench (or the tool
 * whose rail the plan hangs on) offers, built once per plan so every
 * consumer holds the same instance. The whole recipe reads off the
 * blueprint: inputs from the slots, the fastener bill from the
 * fasteners, and the output either the assembled product or the
 * equipment the build grants.
 */

/** A build's operation id: "buildShelf" for the shelf plan. */
export function assemblyOperationId(planId: PlanId): string {
  return `build${planId.charAt(0).toUpperCase()}${planId.slice(1)}`;
}

function makeOperation(record: PlanRecord): Operation {
  // Built while a tool's module is loading, so nothing here reads the
  // blueprint registry until the operation is actually used. The
  // fastener bill is deliberately not `requiredConsumables`: fasteners
  // are spent one at a time as they're driven (sim/commands/
  // assembly-commands.ts), so starting the build costs nothing — the
  // bill reads through `blueprintFastenerCost` where it's shown.
  return {
    id: assemblyOperationId(record.id),
    name: `Build ${record.name}`,
    requiredSkill: record.skill,
    duration: record.duration,
    interaction: { kind: "assembly", blueprint: record.id },
    getInputMaterials: () => blueprintInputs(blueprintFor(record.id)),
    output: (materials: ReadonlyArray<MaterialInstance>) => {
      const blueprint = blueprintFor(record.id);
      switch (record.builds) {
        case "product":
          return {
            inputs: [],
            outputs: [assembleFromBlueprint(blueprint, materials)],
          };
        // Tooling comes off the bench a physical thing, to be carried to
        // its machine and mounted
        case "tool":
          return { inputs: [], outputs: [makeToolItem(record.id as ToolId)] };
        // Machines and upgrades are granted crated / to storage
        case "machine":
          return {
            inputs: [],
            outputs: [],
            machineOutputs: [record.id as never],
          };
        case "upgrade":
          return {
            inputs: [],
            outputs: [],
            upgradeOutputs: [record.id as never],
          };
      }
    },
  };
}

const operations = new Map<PlanId, Operation>();

export function assemblyOperation(planId: PlanId): Operation {
  let operation = operations.get(planId);
  if (!operation) {
    operation = makeOperation(PLAN_RECORDS[planId]);
    operations.set(planId, operation);
  }
  return operation;
}

/**
 * The assembly builds an operation list carries: the bench's own, or a
 * tool's. A build offered by a tool can only be selected where that
 * tool is mounted (tool descriptions promise as much); every build
 * still needs its driving tool in hand to fasten.
 */
export function assemblyOperationsOfferedBy(
  offeredBy: "bench" | ToolId,
): ReadonlyArray<Operation> {
  return PLAN_IDS.filter((id) => PLAN_RECORDS[id].offeredBy === offeredBy).map(
    assemblyOperation,
  );
}
